import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../site/auth";
import Markdown from "../../site/Markdown";
import { useSeo } from "../../site/ui";
import { fmtDate } from "../../site/pages/Forum";
import { LAONA_CATEGORIES } from "../data";

const LAONA_IDS = new Set(LAONA_CATEGORIES.map((c) => c.id));

/* 바이트 → 사람이 읽는 단위 */
function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

function Attachments({ items, loggedIn, from }) {
  if (!items.length) return null;
  return (
    <section className="lb-files" aria-label="첨부 자료">
      <h2>내려받기</h2>
      <ul>
        {items.map((f) => {
          const locked = f.members_only && !loggedIn;
          return (
            <li key={f.id} className="lb-file">
              <div className="lb-file__main">
                <b>
                  {f.title}
                  {f.version && <span className="lb-tag">v{f.version}</span>}
                </b>
                <span className="lb-file__meta">
                  <span className="lb-file__name" title={f.filename}>{f.filename}</span>
                  <span>{formatSize(f.size)}</span>
                  {f.downloads > 0 && <span>{f.downloads.toLocaleString("ko-KR")}회</span>}
                  {f.members_only && <span>회원 전용</span>}
                </span>
              </div>
              {locked ? (
                <Link className="lb-btn" to="/laonabot/login" state={{ from }}>로그인 후 받기</Link>
              ) : (
                <a className="lb-btn lb-btn--primary" href={f.url} download>받기</a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* 비공개(임시 저장) 자료 — 관리자가 읽어 보고 [등록] 으로 공개한다.
   자동 작성된 모듈 글(backend app/module_writer)도 이 상태로 들어온다. */
function ReviewBar({ post, onPublished }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function publish() {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`/api/laona/library/${post.id}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_public: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "등록하지 못했습니다.");
      onPublished();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lb-review" role="region" aria-label="검토">
      <p>
        <b>비공개(임시 저장) 자료입니다.</b> 내용을 확인한 뒤 등록하면 자료실에 공개됩니다.
      </p>
      <div className="lb-review__acts">
        <Link to={`/laonabot/library/${post.id}/edit`} className="lb-btn">수정</Link>
        <button type="button" className="lb-btn lb-btn--primary" onClick={publish} disabled={busy}>
          {busy ? "등록 중…" : "등록"}
        </button>
      </div>
      {error && <p className="lb-review__err">{error}</p>}
    </div>
  );
}

export default function LaonaPost() {
  const { id } = useParams();
  const recipe = useLocation().pathname.startsWith("/laonabot/recipes/");
  const { member } = useAuth();
  const [state, setState] = useState({ status: "loading", post: null });

  useEffect(() => {
    let alive = true;
    setState({ status: "loading", post: null });
    (async () => {
      try {
        const res = await fetch(`/api/forum/${encodeURIComponent(id)}`);
        if (res.status === 404 || res.status === 422) {
          if (alive) setState({ status: "missing", post: null });
          return;
        }
        if (!res.ok) throw new Error();
        const post = await res.json();
        /* 같은 저장소의 I.META 포럼 글은 여기서 보여 주지 않는다 */
        if (alive) setState(LAONA_IDS.has(post.category) ? { status: "ready", post } : { status: "missing", post: null });
      } catch {
        if (alive) setState({ status: "error", post: null });
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const post = state.post;
  const from = recipe ? `/laonabot/recipes/${id}` : `/laonabot/library/${id}`;
  useSeo({
    title: post ? `${post.title} — LaonaBot 자료실` : "자료실 — LaonaBot",
    description: post?.summary || undefined,
    path: from,
  });

  return (
    <article className="lb-section lb-section--top">
      <div className="lb-wrap lb-post">
        {/* 레시피는 비회원도 보는 갤러리로 돌아간다 */}
        {(post?.category === "laona-recipe" || (!post && recipe)) ? (
          <Link to="/laonabot/recipes" className="lb-back">← 레시피 갤러리</Link>
        ) : (
          <Link to={post ? `/laonabot/library?cat=${post.category}` : "/laonabot/library"} className="lb-back">
          ← 자료실{post ? ` · ${post.category_label}` : ""}
        </Link>
        )}

        {state.status === "loading" && <p className="lb-empty">자료를 불러오는 중입니다…</p>}
        {state.status === "error" && <p className="lb-empty">자료를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>}
        {state.status === "missing" && (
          <div className="lb-empty lb-empty--box">
            <b>자료를 찾을 수 없습니다</b>
            <p>삭제되었거나 비공개로 바뀐 자료입니다. <Link to="/laonabot/library">자료실</Link>로 돌아가 주세요.</p>
          </div>
        )}

        {post && (
          <>
            <header className="lb-post__head">
              <div className="lb-card__tags">
                {post.draft && <span className="lb-tag lb-tag--draft">비공개</span>}
                {post.pinned && <span className="lb-tag lb-tag--pin">공지</span>}
                <span className="lb-tag">{post.category_label}</span>
              </div>
              <h1>{post.title}</h1>
              <div className="lb-card__meta">
                <span>{fmtDate(post.created_at)}</span>
                <span>조회 {post.views.toLocaleString("ko-KR")}</span>
                {post.admin && <Link to={`/laonabot/library/${post.id}/edit`} className="lb-linkbtn">수정</Link>}
              </div>
            </header>

            {post.admin && post.draft && (
              <ReviewBar
                post={post}
                onPublished={() => setState((st) => ({ ...st, post: { ...st.post, draft: false } }))}
              />
            )}

            <Markdown source={post.body} className="lb-post__body" />

            <Attachments items={post.attachments} loggedIn={!!member} from={from} />

            {(post.prev || post.next) && (
              <nav className="lb-post__nav" aria-label="이전 · 다음 자료">
                {post.next ? (
                  <Link to={`/laonabot/library/${post.next.id}`}><span>다음 자료</span>{post.next.title}</Link>
                ) : <span />}
                {post.prev ? (
                  <Link to={`/laonabot/library/${post.prev.id}`}><span>이전 자료</span>{post.prev.title}</Link>
                ) : <span />}
              </nav>
            )}
          </>
        )}
      </div>
    </article>
  );
}
